import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { api } from "@/lib/api";
import { ListingCard } from "@/components/ListingCard";
import { ArrowRight, Repeat, ShieldCheck, Coins, Wrench, Package } from "lucide-react";

const CATS = ["📸 Cameras", "💻 Web Design", "👟 Sneakers", "🎸 Guitars", "🔧 Repairs", "🎨 Art", "📚 Books", "🚲 Bikes", "🎧 Audio", "🧑‍🍳 Lessons"];

export default function Landing() {
  const [listings, setListings] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/listings").then(({ data }) => setListings(data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative bg-black text-white border-b-2 border-[#E6B31E] overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
          <div className="inline-flex items-center gap-2 bg-[#E6B31E] text-black font-extrabold text-xs rounded-full px-4 py-1.5 mb-6">
            <Repeat size={14} strokeWidth={3} /> TRADE ANYTHING FOR ANYTHING
          </div>
          <h1 className="font-display font-black tracking-tighter text-5xl sm:text-7xl leading-[0.95] max-w-4xl">
            Swap goods & services on <span className="text-[#E6B31E]">Tradeable Exchange</span>
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-2xl font-medium">
            List any item or skill, propose a trade, agree in chat, and seal the deal. No middleman, no commission — completely free to trade.
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link to="/browse" data-testid="hero-browse-btn" className="flex items-center gap-2 bg-[#E6B31E] text-black font-extrabold rounded-full px-7 py-3.5 border-2 border-[#E6B31E] hover:bg-[#f5c841] transition-colors">
              Browse trades <ArrowRight size={18} strokeWidth={3} />
            </Link>
            <Link to="/create" data-testid="hero-list-btn" className="flex items-center gap-2 bg-transparent text-white font-extrabold rounded-full px-7 py-3.5 border-2 border-white/40 hover:border-[#E6B31E] hover:text-[#E6B31E] transition-colors">
              List an item
            </Link>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="bg-[#E6B31E] border-b-2 border-black py-3">
        <Marquee speed={45} gradient={false}>
          {CATS.concat(CATS).map((c, i) => (
            <span key={i} className="font-display font-extrabold text-black text-lg mx-6">{c}</span>
          ))}
        </Marquee>
      </div>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <h2 className="font-display font-extrabold text-3xl tracking-tight mb-8">How it works</h2>
        <div className="grid gap-6 sm:grid-cols-3">
          {[
            { icon: Package, t: "List anything", d: "Post goods or services with photos and an estimated value. Say what you want in return." },
            { icon: Repeat, t: "Propose & agree", d: "Send a trade offer, negotiate in the trade room chat until both parties agree." },
            { icon: Coins, t: "Seal the deal", d: "Owner accepts, the trade is locked in, and you arrange the swap. Zero fees." },
          ].map((s, i) => (
            <div key={i} className="bg-white nb-border rounded-2xl p-7 nb-shadow">
              <div className="w-12 h-12 rounded-xl bg-black text-[#E6B31E] flex items-center justify-center mb-4">
                <s.icon size={24} strokeWidth={2.5} />
              </div>
              <h3 className="font-display font-extrabold text-xl">{s.t}</h3>
              <p className="text-black/60 font-medium mt-2 text-sm">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured */}
      {listings.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-20">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-display font-extrabold text-3xl tracking-tight">Fresh on the exchange</h2>
            <Link to="/browse" className="font-bold text-sm flex items-center gap-1 hover:text-[#B8860B]">View all <ArrowRight size={16} strokeWidth={3} /></Link>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 items-start">
            {listings.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
          </div>
        </section>
      )}

      <footer className="bg-black text-white/60 border-t-2 border-[#E6B31E] py-8 text-center text-sm font-medium">
        <div className="flex items-center justify-center gap-2 mb-2">
          <img src="/logo.png" alt="" className="w-8 h-8 rounded" />
          <span className="font-display font-extrabold text-[#E6B31E]">TRADEABLE EXCHANGE</span>
        </div>
        Trade anything. Zero commission. © 2026
        <div className="mt-2">
          <Link to="/support" data-testid="footer-contact-link" className="text-[#E6B31E] font-bold hover:underline">Contact the House</Link>
        </div>
      </footer>
    </div>
  );
}
